import { OpenNewDayModalProps } from '@/renderer/types/sales';
import { X } from 'lucide-react';
import React from 'react';
import { DatePicker } from '../common/DatePicker';
import { Button } from '../common';

export const OpenNewDayModal: React.FC<OpenNewDayModalProps> = ({
  isOpen,
  currentBusinessDayLabel,
  selectedDate,
  minDate,
  maxDate,
  isSubmitting = false,
  onClose,
  onDateChange,
  onSubmit,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isSubmitting ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close open new day modal"
      />

      <div className="relative w-full max-w-lg rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold text-black">Open New Day</h2>
            <p className="mt-1 text-sm text-black/70">
              Current business day: {currentBusinessDayLabel}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black disabled:opacity-50"
            aria-label="Close open new day modal"
          >
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-6">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-black/80">
            Select a future business date. We will verify there are no running
            orders before switching the outlet to the next day.
          </div>

          <DatePicker
            mode="single"
            id="open-new-day-date"
            label="New business day"
            value={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            onChange={onDateChange}
          />
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <Button
            type="button"
            variant="outline"
            size="md"
            disabled={isSubmitting}
            onClick={onClose}
            className="border-gray-300 text-black hover:border-gray-400 hover:bg-gray-100 hover:text-black"
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="primary"
            size="md"
            isLoading={isSubmitting}
            disabled={isSubmitting || !selectedDate}
            onClick={onSubmit}
            className="min-w-[140px] bg-primary text-white hover:bg-primary/90"
          >
            Open Day
          </Button>
        </div>
      </div>
    </div>
  );
};
